import { LockSimple } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";

import { useAnalytics } from "../utils/analytics";
import { useAppDispatch, useAppSelector } from "../utils/hooks";
import { setAuthenticationDialogOpen } from "../utils/slices/ui";

export interface CommentLoginWallProps {
  /**
   * Total number of real comments that exist for this section. Drives the
   * number of blurred placeholder cards rendered (capped at 3 so long
   * threads don't produce a noisy wall of ghosts).
   */
  count: number;
  /**
   * Which comment section this wall is replacing. Only used for analytics /
   * testing breadcrumbs so we can tell pending vs previous vs required-
   * changes vs suggested-improvements apart in click events.
   */
  section?:
    | "comments"
    | "pending-comments"
    | "previous-comments"
    | "required-changes"
    | "suggested-improvements";
}

const MAX_PLACEHOLDERS = 3;

/**
 * A blurred-comment paywall shown on share-link views when the viewer is not
 * signed in. This adds a visual + interaction layer that makes the lockout
 * obvious and routes a click into the sign-in flow.
 */
export function CommentLoginWall({
  count,
  section = "comments",
}: CommentLoginWallProps) {
  const dispatch = useAppDispatch();
  const posthog = useAnalytics("CommentLoginWall");
  const guestUser = useAppSelector((state) => state.auth.value);
  const placeholderCount = Math.max(1, Math.min(count || 1, MAX_PLACEHOLDERS));

  function openAuth() {
    posthog.capture("share_link_login_wall_clicked", {
      section,
      ticketId: guestUser?.ticketId,
      enterpriseId: guestUser?.enterpriseId,
    });
    dispatch(setAuthenticationDialogOpen(true));
  }

  return (
    <div
      role="region"
      aria-label="Comments are locked. Log in to view."
      data-testid="comment-login-wall"
      data-section={section}
      className="relative flex flex-col gap-3 p-3"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none flex select-none flex-col gap-3"
      >
        {Array.from({ length: placeholderCount }).map((_, idx) => (
          <div
            key={idx}
            className="bg-white flex items-start gap-3 rounded-md border border-puntt-neutral-gray-6 p-3 blur-sm"
          >
            <div className="size-6 rounded-full bg-puntt-neutral-gray-4" />
            <div className="flex flex-1 flex-col gap-2">
              <div className="h-3 w-24 rounded bg-puntt-neutral-gray-4" />
              <div className="flex flex-col gap-1">
                <div className="h-2 w-full rounded bg-puntt-neutral-gray-3" />
                <div className="h-2 w-11/12 rounded bg-puntt-neutral-gray-3" />
                <div className="h-2 w-3/4 rounded bg-puntt-neutral-gray-3" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 px-4 text-center">
        <LockSimple
          size={24}
          weight="regular"
          className="text-puntt-neutral-gray-11"
          aria-hidden="true"
        />
        <Text size="2" color="gray" className="max-w-xs">
          Comments are only visible to signed-in teammates.
        </Text>
        <button
          type="button"
          onClick={openAuth}
          data-auth-trigger="view-comments"
          data-testid="comment-login-wall-cta"
          className="pointer-events-auto cursor-pointer bg-transparent p-0 text-sm font-medium text-puntt-blue-11 underline underline-offset-2 hover:text-puntt-blue-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-puntt-blue-9 focus-visible:ring-offset-2"
        >
          Log in to see comments
        </button>
      </div>
    </div>
  );
}

export default CommentLoginWall;
